import React, { useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Calendar, Clock, ArrowRight, Search, Tag, User } from "lucide-react";
import { BLOG_POSTS } from "../mock/mock";

const CATEGORIES = ["All", "Destinations", "Travel Tips", "Guides", "Budget Travel", "Itineraries"];

const BlogPage = () => {
  const [cat, setCat] = useState("All");
  const [query, setQuery] = useState("");

  const filtered = BLOG_POSTS.filter((p) => {
    if (cat !== "All" && p.category !== cat) return false;
    if (query && !`${p.title} ${p.excerpt} ${p.tags.join(" ")}`.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <>
      <Header />
      <main>
        <section className="relative pt-40 pb-20 md:pt-48 md:pb-24 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1500835556837-99ac94a94552?w=1920&q=80"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 to-slate-900/50" />
          <div className="relative max-w-7xl mx-auto px-6 text-white">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white text-xs font-semibold tracking-wider uppercase">
              <Tag className="w-3.5 h-3.5" /> Travel Journal
            </div>
            <h1 className="mt-4 text-4xl md:text-6xl font-bold tracking-tight max-w-3xl" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Stories, guides & tips from the road
            </h1>
            <p className="mt-4 text-white/85 text-lg max-w-2xl">
              Real travel wisdom from our team of experts, guides, and 15,000+ PMR travellers.
            </p>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            {/* Filters */}
            <div className="flex flex-col lg:flex-row gap-4 justify-between mb-10">
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((c) => (
                  <button
                    key={c}
                    onClick={() => setCat(c)}
                    className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition ${
                      cat === c
                        ? "bg-teal-600 text-white border-teal-600"
                        : "bg-white text-slate-700 border-slate-200 hover:border-teal-300"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
              <div className="relative max-w-xs w-full">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search articles..."
                  className="pl-9"
                />
              </div>
            </div>

            {featured && (
              <Link
                to={`/blog/${featured.slug}`}
                className="group grid md:grid-cols-2 gap-6 rounded-2xl overflow-hidden border border-slate-200 hover:shadow-2xl transition mb-12"
              >
                <div className="relative aspect-[4/3] md:aspect-auto overflow-hidden">
                  <img src={featured.image} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                  <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-orange-500 text-white text-xs font-bold uppercase tracking-wider">Featured</div>
                </div>
                <div className="p-6 md:p-10 flex flex-col justify-center">
                  <div className="inline-flex items-center gap-2 text-xs font-semibold text-teal-700 uppercase tracking-wider">
                    <Tag className="w-3 h-3" /> {featured.category}
                  </div>
                  <h2 className="mt-3 text-2xl md:text-4xl font-bold text-slate-900 leading-tight" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    {featured.title}
                  </h2>
                  <p className="mt-3 text-slate-600 text-lg">{featured.excerpt}</p>
                  <div className="mt-5 flex items-center gap-4 text-xs text-slate-500">
                    <span className="flex items-center gap-1"><User className="w-3 h-3" /> {featured.author}</span>
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {featured.date}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {featured.readTime}</span>
                  </div>
                  <div className="mt-6 inline-flex items-center gap-1 text-teal-700 font-semibold group-hover:text-orange-500 transition">
                    Read article <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
                  </div>
                </div>
              </Link>
            )}

            {rest.length > 0 && (
              <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                {rest.map((p) => (
                  <Link
                    key={p.id}
                    to={`/blog/${p.slug}`}
                    className="group bg-white rounded-2xl overflow-hidden border border-slate-200 hover:shadow-xl hover:-translate-y-0.5 transition"
                  >
                    <div className="relative h-52 overflow-hidden">
                      <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-110 transition duration-500" />
                    </div>
                    <div className="p-5">
                      <div className="inline-flex items-center gap-1 text-[11px] font-bold text-teal-700 uppercase tracking-wider">
                        <Tag className="w-3 h-3" /> {p.category}
                      </div>
                      <h3 className="mt-2 text-lg font-bold text-slate-900 leading-snug group-hover:text-teal-700 transition">
                        {p.title}
                      </h3>
                      <p className="mt-2 text-sm text-slate-600 line-clamp-2">{p.excerpt}</p>
                      <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                        <span>{p.author}</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {p.readTime}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {filtered.length === 0 && (
              <div className="text-center py-20">
                <p className="text-slate-500">No articles match your search. Try a different keyword.</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default BlogPage;
